import React, { useState, useEffect } from 'react';
import { adminApi } from '../../../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ACCENT, BLUE, GREEN, Spinner } from './shared';

export function HoursTab() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    adminApi.get('/admin/analytics/business?period=month').then(r => setD(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  if (loading) return <Spinner />;
  const hourly = d?.hourly || {};
  const heatmap = hourly.heatmap || [];
  const byHour = hourly.by_hour || [];
  const byDow = hourly.by_dow || [];

  const dowNames = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const heatmapGrid = {};
  let maxVisits = 0;
  heatmap.forEach(h => {
    const key = `${h.day_idx}-${h.hour}`;
    heatmapGrid[key] = h.visits;
    if (h.visits > maxVisits) maxVisits = h.visits;
  });

  return (
    <div className="space-y-6" data-testid="hours-tab">
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
        <h3 className="font-semibold text-[#1A1A1A] mb-1">Heatmap visite (ultimi 30 giorni)</h3>
        <p className="text-[10px] text-[#64748B] mb-4">Intensit{'\u00E0'} delle visite per giorno della settimana e ora</p>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" data-testid="heatmap-table">
            <thead>
              <tr>
                <th className="text-[9px] text-[#64748B] p-1 text-left w-10"></th>
                {hours.filter(h => h >= 8 && h <= 23).map(h => (
                  <th key={h} className="text-[9px] text-[#64748B] p-1 text-center">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dowNames.map((day, di) => (
                <tr key={day}>
                  <td className="text-[10px] text-[#64748B] font-medium p-1">{day}</td>
                  {hours.filter(h => h >= 8 && h <= 23).map(h => {
                    const v = heatmapGrid[`${di}-${h}`] || 0;
                    const intensity = maxVisits > 0 ? v / maxVisits : 0;
                    return (
                      <td key={h} className="p-0.5" title={`${day} ${h}:00 \u2014 ${v} visite`}>
                        <div
                          className="w-full aspect-square rounded-sm min-w-[20px] transition-colors"
                          style={{ backgroundColor: intensity > 0 ? `rgba(212, 78, 32, ${0.15 + intensity * 0.85})` : '#F5F5F5' }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-2 mt-3 justify-end">
          <span className="text-[9px] text-[#64748B]">Meno</span>
          {[0, 0.25, 0.5, 0.75, 1].map((i, idx) => (
            <div key={idx} className="w-4 h-4 rounded-sm" style={{ backgroundColor: i === 0 ? '#F5F5F5' : `rgba(212, 78, 32, ${0.15 + i * 0.85})` }} />
          ))}
          <span className="text-[9px] text-[#64748B]">Pi{'\u00F9'}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {byHour.length > 0 && (
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <h3 className="font-semibold text-[#1A1A1A] mb-4">Visite per ora</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={byHour} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#888' }} tickFormatter={h => `${h}:00`} />
                <YAxis tick={{ fontSize: 10, fill: '#888' }} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="visits" name="Visite" fill={ACCENT} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {byDow.length > 0 && (
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <h3 className="font-semibold text-[#1A1A1A] mb-4">Visite per giorno</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={byDow} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#888' }} />
                <YAxis tick={{ fontSize: 10, fill: '#888' }} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="visits" name="Visite" fill={BLUE} radius={[4, 4, 0, 0]} />
                <Bar dataKey="revenue" name="Ricavi (\u20AC)" fill={GREEN} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
